import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {sendCustomMetric} from '@github-ui/stats'

import {getActivationCorrelation} from './activation-correlation'
import type {ClientInteractionFact, ClientInteractionUnknown} from './client-interaction-contract'
import type {ClientRecoveryEvent, ClientRecoveryFact, RecoveryReason} from './client-recovery-contract'
import {
  failed,
  interactionFailureReason,
  interactionUnresolvedReason,
  NO_REASONS,
  presentationFailureReason,
  presentationUnresolvedReason,
  readinessFailureReason,
  recoveryFailureReason,
  recoveryUnresolvedReason,
  unresolved,
  type OutcomeReasons,
  type UnresolvedReason,
} from './outcome-reasons'
import {boundOpenState, metricSurface, type MetricOpenState} from './metric-surface'
import {taskRepositoryScope, type RepositoryScope} from './repository-scope'
import {startTypeFor, type StartType} from './start-type'
import type {
  ResultPresentationEvent,
  ResultPresentationFact,
  ResultPresentationUnknown,
} from './result-presentation-contract'
import type {UiReadinessFact, UiReadinessNavigationType, UiReadinessSurface} from './ui-readiness-contract'

export type MetricSender = (metric: Parameters<typeof sendCustomMetric>[0], flushImmediately?: boolean) => void
type Outcome = 'success' | 'failure' | 'unresolved' | 'coverage_lost'
type CorrelatableFact = {
  task_id?: string
  session_id?: string
  environment_id?: string
}

const MAX_ACTIVE_METRIC_UNITS = 512
const MAX_FINALIZED_METRIC_IDS = 512

function uiVariant() {
  return copilotFeatureFlags.chatSurfacesRewrite ? 'chat_surfaces_rewrite' : 'legacy'
}

export function createEmitter(send: MetricSender, reportError: () => void) {
  return (metric: Parameters<MetricSender>[0], fact?: CorrelatableFact, correlation?: Record<string, string>) => {
    try {
      const activation = getActivationCorrelation(fact?.task_id)
      const rumContext = {
        ...(fact?.task_id ? {task_id: fact.task_id} : {}),
        ...(fact?.session_id ? {session_id: fact.session_id} : {}),
        ...(fact?.environment_id ? {environment_id: fact.environment_id} : {}),
        ...correlation,
        ...activation,
      }
      send({
        ...metric,
        tags: {...metric.tags, ui_variant: uiVariant()},
        ...(Object.keys(rumContext).length > 0 ? {rumContext} : {}),
      })
    } catch {
      reportError()
    }
  }
}

type TaggableFact = Parameters<typeof boundOpenState>[0] & {
  surface: UiReadinessSurface
  role: string
  turn_id?: string
  repository_scope?: RepositoryScope
}

interface CommonTagOverrides {
  openState?: MetricOpenState
  startType?: StartType
}

export function commonTags(
  fact: TaggableFact,
  {openState = boundOpenState(fact), startType = startTypeFor(fact)}: CommonTagOverrides = {},
) {
  const knownScope = fact.repository_scope && fact.repository_scope !== 'unknown' ? fact.repository_scope : undefined
  return {
    surface: metricSurface(fact.surface, openState),
    role: fact.role,
    repository_scope: knownScope ?? taskRepositoryScope(fact.task_id),
    start_type: startType,
  }
}

function rememberFinalized(finalized: Set<string>, id: string) {
  finalized.add(id)
  if (finalized.size <= MAX_FINALIZED_METRIC_IDS) return
  const oldest = finalized.values().next().value
  if (oldest) finalized.delete(oldest)
}

function evictOldest<T>(active: Map<string, T>, finalize: (id: string, value: T) => void) {
  if (active.size <= MAX_ACTIVE_METRIC_UNITS) return
  const oldest = active.entries().next().value
  if (oldest) finalize(...oldest)
}

interface UiReadinessMetricState {
  open: UiReadinessFact
  latest: UiReadinessFact
  navigationType: UiReadinessNavigationType
}

export function createUiReadinessMetricObserver(
  send: MetricSender = sendCustomMetric,
  reportError: () => void = () => undefined,
) {
  const emit = createEmitter(send, reportError)
  const active = new Map<string, UiReadinessMetricState>()
  const finalized = new Set<string>()
  const tags = (fact: UiReadinessFact, navigationType: UiReadinessNavigationType) => ({
    ...commonTags(fact),
    entry_kind: fact.entry_kind,
    navigation_type: navigationType,
  })

  const outcome = (
    fact: UiReadinessFact,
    value: Outcome,
    reasons: OutcomeReasons,
    navigationType: UiReadinessNavigationType,
  ) => {
    const id = fact.client_open_id
    if (finalized.has(id)) return
    active.delete(id)
    rememberFinalized(finalized, id)
    emit(
      {
        name: 'ONE_COPILOT_WEB_CLIENT_READINESS_OUTCOME',
        value: 1,
        tags: {...tags(fact, navigationType), outcome: value, ...reasons},
      },
      fact,
    )
  }
  const evicted = (_id: string, state: UiReadinessMetricState) =>
    outcome(state.latest, 'unresolved', unresolved('other'), state.navigationType)

  return {
    observe(fact: UiReadinessFact, navigationType?: UiReadinessNavigationType) {
      const id = fact.client_open_id
      if (finalized.has(id)) return
      if (fact.observation === 'open') {
        active.set(id, {open: fact, latest: fact, navigationType: navigationType ?? 'unknown'})
        evictOldest(active, evicted)
      } else {
        const state = active.get(id)
        if (state) {
          state.latest = fact
          if (navigationType) state.navigationType = navigationType
        }
        const navigation = navigationType ?? state?.navigationType ?? 'unknown'
        if (fact.observation === 'input_ready') {
          outcome(fact, 'success', NO_REASONS, navigation)
          const start =
            fact.origin_clock_id === fact.clock_id && fact.origin_monotonic_ms !== undefined
              ? fact.origin_monotonic_ms
              : state?.open.clock_id === fact.clock_id
                ? state.open.monotonic_ms
                : undefined
          if (start !== undefined && fact.monotonic_ms >= start) {
            emit(
              {
                name: 'ONE_COPILOT_WEB_CLIENT_READINESS_LATENCY',
                value: fact.monotonic_ms - start,
                tags: tags(fact, navigation),
              },
              fact,
            )
          }
        } else if (fact.observation === 'failure') {
          outcome(fact, 'failure', failed(readinessFailureReason(fact.reason)), navigation)
        } else if (fact.observation === 'page_exit') {
          outcome(fact, 'unresolved', unresolved('page_exit'), navigation)
        } else if (fact.observation === 'view_disposed' && fact.reason === 'closed') {
          outcome(fact, 'unresolved', unresolved('disposed'), navigation)
        }
      }
    },
  }
}

interface ClientInteractionMetricState {
  latest: ClientInteractionFact
  openState: MetricOpenState
  unresolvedReason?: ClientInteractionUnknown
}

// The committed context shows whether the command was sent from a new chat or into an open one.
function committedOpenState(fact: ClientInteractionFact): MetricOpenState {
  return fact.session_id || fact.task_id ? 'chat' : 'new'
}

export function createClientInteractionMetricObserver(
  send: MetricSender = sendCustomMetric,
  reportError: () => void = () => undefined,
) {
  const emit = createEmitter(send, reportError)
  const active = new Map<string, ClientInteractionMetricState>()
  const finalized = new Set<string>()
  const tags = (fact: ClientInteractionFact, openState: MetricOpenState) => ({
    ...commonTags(fact, {openState}),
    interaction_kind: fact.interaction_kind,
    handoff_required:
      fact.handoff_scope === 'single_action' || fact.handoff_scope === 'send_now_and_remove_queued' ? 'true' : 'false',
  })
  const outcome = (
    fact: ClientInteractionFact,
    value: Outcome,
    reasons: OutcomeReasons,
    openState: MetricOpenState,
  ) => {
    const id = fact.client_interaction_id
    if (finalized.has(id)) return false
    active.delete(id)
    rememberFinalized(finalized, id)
    emit(
      {
        name: 'ONE_COPILOT_WEB_CLIENT_INTERACTION_OUTCOME',
        value: 1,
        tags: {...tags(fact, openState), outcome: value, ...reasons},
      },
      fact,
    )
    return true
  }
  // Unresolved evidence stays open for a later completion, so the last reported gap explains
  // the outcome when the interaction is finally retired.
  const retire = (state: ClientInteractionMetricState, fallback: UnresolvedReason) =>
    outcome(
      state.latest,
      'unresolved',
      unresolved(state.unresolvedReason ? interactionUnresolvedReason(state.unresolvedReason) : fallback),
      state.openState,
    )

  return {
    observe(fact: ClientInteractionFact) {
      const id = fact.client_interaction_id
      if (finalized.has(id)) return
      if (fact.observation === 'committed') {
        active.set(id, {latest: fact, openState: committedOpenState(fact)})
        evictOldest(active, (_id, state) => retire(state, 'other'))
      } else {
        const state = active.get(id)
        if (state) {
          state.latest = fact
          if (fact.observation === 'unresolved') state.unresolvedReason = fact.reason
        }
        const openState = state?.openState ?? 'unknown'
        if (fact.observation === 'failed') {
          outcome(fact, 'failure', failed(interactionFailureReason(fact.reason)), openState)
        } else if (fact.observation === 'completed' && outcome(fact, 'success', NO_REASONS, openState)) {
          if (fact.monotonic_ms >= fact.input_start_ms) {
            emit(
              {
                name: 'ONE_COPILOT_WEB_CLIENT_INTERACTION_LATENCY',
                value: fact.monotonic_ms - fact.input_start_ms,
                tags: tags(fact, openState),
              },
              fact,
            )
          }
        }
      }
    },
    pageExit() {
      for (const state of active.values()) retire(state, 'page_exit')
      active.clear()
    },
  }
}

interface ResultPresentationMetricState {
  latest: ResultPresentationFact
  completion?: ResultPresentationFact
  failure?: ResultPresentationFact
  unresolvedReason?: ResultPresentationUnknown
}

type PresentationTurns = {
  contentReceipt(fact: ResultPresentationFact): void
  contentPresented(completion: Extract<ResultPresentationFact, {observation: 'completed'}>): void
  contentEnded(turnId: string, outcome: 'failure' | 'unresolved', reasons: OutcomeReasons): void
  contentReleased(turnId: string): void
}

export function createResultPresentationMetricObserver(
  send: MetricSender = sendCustomMetric,
  reportError: () => void = () => undefined,
  turns?: PresentationTurns,
) {
  const emit = createEmitter(send, reportError)
  const active = new Map<string, ResultPresentationMetricState>()
  const finalized = new Set<string>()
  const tags = (fact: ResultPresentationFact) => ({
    ...commonTags(fact),
    receipt_kind: fact.milestone_kind,
  })
  const outcome = (fact: ResultPresentationFact, value: Outcome, reasons: OutcomeReasons) => {
    const id = fact.receipt_event_id
    if (finalized.has(id)) return
    active.delete(id)
    rememberFinalized(finalized, id)
    emit(
      {
        name: 'ONE_COPILOT_WEB_RESULT_PRESENTATION_OUTCOME',
        value: 1,
        tags: {...tags(fact), outcome: value, ...reasons},
      },
      fact,
    )
  }
  const unresolvedReason = (state: ResultPresentationMetricState, fallback: UnresolvedReason): UnresolvedReason => {
    if (state.completion?.visibility_interrupted) return 'hidden'
    if (state.unresolvedReason) return presentationUnresolvedReason(state.unresolvedReason)
    return fallback
  }
  const finalize = (state: ResultPresentationMetricState, fallback: UnresolvedReason) => {
    const {completion, failure, latest} = state
    const content = latest.milestone_kind === 'content'
    if (
      completion?.observation === 'completed' &&
      !completion.visibility_interrupted &&
      (failure?.observation !== 'failed' || failure.monotonic_ms > completion.closure_time_ms)
    ) {
      outcome(completion, 'success', NO_REASONS)
      if (completion.closure_time_ms >= completion.receipt_monotonic_ms) {
        emit(
          {
            name: 'ONE_COPILOT_WEB_RESULT_PRESENTATION_LATENCY',
            value: completion.closure_time_ms - completion.receipt_monotonic_ms,
            tags: tags(completion),
          },
          completion,
        )
      }
      if (content) turns?.contentPresented(completion)
    } else if (
      failure?.observation === 'failed' &&
      (completion?.observation !== 'completed' || failure.monotonic_ms <= completion.closure_time_ms)
    ) {
      const reasons = failed(presentationFailureReason(failure.reason))
      outcome(failure, 'failure', reasons)
      if (content) turns?.contentEnded(latest.turn_id, 'failure', reasons)
    } else {
      const reasons = unresolved(unresolvedReason(state, fallback))
      outcome(latest, 'unresolved', reasons)
      // A replaced view hands the turn to its successor, such as the task page after a redirect.
      if (content && state.unresolvedReason === 'replaced' && !state.completion?.visibility_interrupted)
        turns?.contentReleased(latest.turn_id)
      else if (content) turns?.contentEnded(latest.turn_id, 'unresolved', reasons)
    }
  }

  return {
    observe(fact: ResultPresentationEvent) {
      if (fact.observation === 'coverage_lost') {
        emit(
          {
            name: 'ONE_COPILOT_WEB_RESULT_PRESENTATION_OUTCOME',
            value: 1,
            tags: {...commonTags(fact), outcome: 'coverage_lost', ...NO_REASONS},
          },
          fact,
        )
        return
      }
      const id = fact.receipt_event_id
      if (finalized.has(id)) return
      const state = active.get(id)
      if (fact.observation === 'receipt') {
        active.set(id, {latest: fact})
        if (fact.milestone_kind === 'content') turns?.contentReceipt(fact)
        evictOldest(active, (_id, evictedState) => finalize(evictedState, 'other'))
      } else if (state) {
        state.latest = fact
        if (fact.observation === 'completed') state.completion = fact
        else if (fact.observation === 'unresolved') {
          state.unresolvedReason = fact.reason
          if (fact.final) finalize(state, 'other')
        } else if (fact.observation === 'failed' && (!state.failure || fact.monotonic_ms < state.failure.monotonic_ms))
          state.failure = fact
      }
    },
    dispose() {
      for (const state of active.values()) finalize(state, 'disposed')
      active.clear()
    },
  }
}

interface ClientRecoveryMetricState {
  latest: ClientRecoveryFact
  completion?: ClientRecoveryFact
  failure?: ClientRecoveryFact
  blockedReason?: RecoveryReason
}

export function createClientRecoveryMetricObserver(
  send: MetricSender = sendCustomMetric,
  reportError: () => void = () => undefined,
) {
  const emit = createEmitter(send, reportError)
  const active = new Map<string, ClientRecoveryMetricState>()
  const finalized = new Set<string>()
  const tags = (fact: ClientRecoveryFact) => ({
    ...commonTags(fact),
    recovery_path: fact.recovery_path,
    trigger: fact.trigger,
  })
  const outcome = (fact: ClientRecoveryFact, value: Outcome, reasons: OutcomeReasons) => {
    const id = fact.episode_id
    if (finalized.has(id)) return
    active.delete(id)
    rememberFinalized(finalized, id)
    emit(
      {
        name: 'ONE_COPILOT_WEB_CLIENT_RECOVERY_OUTCOME',
        value: 1,
        tags: {...tags(fact), outcome: value, ...reasons},
      },
      fact,
    )
  }
  const finalize = (state: ClientRecoveryMetricState, fallback: UnresolvedReason) => {
    const {completion, failure, latest} = state
    if (
      completion?.observation === 'completed' &&
      (failure?.observation !== 'failed' || failure.monotonic_ms > completion.closure_time_ms)
    ) {
      outcome(completion, 'success', NO_REASONS)
      if (completion.closure_time_ms >= completion.episode_monotonic_ms) {
        emit(
          {
            name: 'ONE_COPILOT_WEB_CLIENT_RECOVERY_LATENCY',
            value: completion.closure_time_ms - completion.episode_monotonic_ms,
            tags: tags(completion),
          },
          completion,
        )
      }
    } else if (failure?.observation === 'failed') {
      outcome(failure, 'failure', failed(recoveryFailureReason(failure.stage)))
    } else {
      outcome(
        latest,
        'unresolved',
        unresolved(state.blockedReason ? recoveryUnresolvedReason(state.blockedReason) : fallback),
      )
    }
  }

  return {
    observe(fact: ClientRecoveryEvent) {
      if (fact.observation === 'coverage_lost') {
        emit(
          {
            name: 'ONE_COPILOT_WEB_CLIENT_RECOVERY_OUTCOME',
            value: 1,
            tags: {...commonTags(fact), outcome: 'coverage_lost', ...NO_REASONS},
          },
          fact,
        )
        return
      }
      const id = fact.episode_id
      if (finalized.has(id)) return
      const state = active.get(id)
      if (fact.observation === 'episode_started') {
        active.set(id, {latest: fact})
        evictOldest(active, (_id, evictedState) => finalize(evictedState, 'other'))
      } else if (state) {
        state.latest = fact
        if (fact.observation === 'completed') state.completion = fact
        else if (fact.observation === 'blocked') state.blockedReason = fact.reason
        else if (fact.observation === 'failed' && (!state.failure || fact.monotonic_ms < state.failure.monotonic_ms))
          state.failure = fact
      }
    },
    dispose() {
      for (const state of active.values()) finalize(state, 'disposed')
      active.clear()
    },
  }
}
